import { RadioSelectProps, RadioSelectRadioProps, RadioSelectSelectProps } from '../Selection';
import { ReactNode } from 'react';
import { LookupValue, MessageLevel } from '../../types/types';

/**
 * Allowed types for single-value based responses.
 */
export type ResponseValueType = boolean | number | string;

/**
 * Allowed types for multi-value based responses.
 */
export type ResponseMultiValueType = number | string;

export type NonNullConditionalTrigger<TValue extends ResponseValueType> =
  | ((value?: TValue[] | TValue | null) => boolean)
  | TValue[]
  | TValue;
export type ConditionalTrigger<TValue extends ResponseValueType> = NonNullConditionalTrigger<TValue> | [] | null;
export type ConditionalHelpMessage<TValue extends ResponseValueType> =
  | ReactNode
  | ((value: TValue[] | TValue | null) => ReactNode);

export interface DownloadLink {
  url: string;
  displayText: string;
}

/**
 * Match mode to use when the trigger value is `null`. May be:
 *
 * - `'none'`: Matches when the user has not answered the question
 * - `'any'`: Matches when the user has provided any answer to the question
 * - `'always'`: Matches at all times (ie. both answered and unanswered questions).
 */
export type NullTriggerMatchType = 'none' | 'any' | 'always';

export type NullConditionallyTriggered<TValue extends ResponseValueType> = {
  trigger?: null | [];
  nullTriggerMatchType?: NullTriggerMatchType;
};

export type NonNullConditionallyTriggered<TValue extends ResponseValueType> = {
  trigger: NonNullConditionalTrigger<TValue>;
};

export type ConditionallyTriggered<TValue extends ResponseValueType> =
  | NullConditionallyTriggered<TValue>
  | NonNullConditionallyTriggered<TValue>;

export type SupportingDocument<TValue extends ResponseValueType> = ConditionallyTriggered<TValue> & {
  id?: string;
  title: string;
  helpMessage?: ConditionalHelpMessage<TValue>;
  downloadLink?: DownloadLink | DownloadLink[];
};

export type QuestionConditionalMessage<TValue extends ResponseValueType> = ConditionallyTriggered<TValue> & {
  id?: string;
  title: string;
  helpMessage?: ConditionalHelpMessage<TValue>;
  level?: MessageLevel;
};

export interface QuestionProps<TValue extends ResponseValueType> {
  id?: string;
  name: string;
  label: string;
  helpMessage?: ReactNode;
  optional?: boolean;

  value?: TValue;
  onChange: (next: TValue) => void;
  errorMessage?: string;

  conditionalMessages?: QuestionConditionalMessage<TValue>[];
  supportingDocuments?: SupportingDocument<TValue>[];
}

export interface NumberQuestionProps {
  min?: number;
  max?: number;
}

export interface RangeQuestionOption<TValue extends ResponseValueType = string> extends LookupValue<TValue> {
  helpMessage?: ReactNode;
}

export type RangeQuestionProps<TValue extends ResponseValueType> = Pick<
  RadioSelectProps<TValue>,
  'radioProps' | 'maxRadioButtons'
> & {
  options: RangeQuestionOption<TValue>[];
  selectProps?: Pick<RadioSelectSelectProps<TValue>, 'disabled' | 'className' | 'placeholder'>;
};

export type MultiValueQuestionProps = {
  /**
   * All the values selected for this question.
   */
  value: string[];

  /**
   * Invoked when a single value is being toggled.
   *
   * @param nextValues The updated array of values with the toggled value included/excluded as appropriate.
   * @param toggleValue The value that the user has toggled on/off.
   */
  onChange: (nextValues: string[], toggleValue: string) => void;

  /**
   * The options the user can select from.
   */
  options: RangeQuestionOption[];
};

export type YesNoQuestionProps = RadioSelectRadioProps<boolean>;

export interface TextQuestionProps {
  responseLength?: 'short' | 'medium' | 'long';
  inputWidth?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  maxLength?: number;
}

export const YES_NO_OPTIONS: LookupValue<boolean>[] = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];
